import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BarChart3, Maximize2, Minimize2, Plus, Pencil, Trash2, X } from 'lucide-react'
import { PowerBIEmbed } from 'powerbi-client-react'
import { models, type Embed } from 'powerbi-client'

import { apiClient } from '../services/api'
import { useOrganization } from '../contexts/OrganizationContext'
import LoadingSpinner from '../components/LoadingSpinner'

interface Dashboard {
  id: string
  name: string
  description: string | null
  powerbi_report_id: string
  is_active: boolean
  display_order: number
}

interface EmbedConfig {
  report_id: string
  embed_url: string
  embed_token: string
  token_expiry: string
}

interface UserInfo {
  role: string
  is_system_admin_org: boolean
}

export default function ReportsPage() {
  const { t } = useTranslation('reports')
  const { selectedOrgId } = useOrganization()
  const queryClient = useQueryClient()
  const [selectedDashboard, setSelectedDashboard] = useState<string | null>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showManageDialog, setShowManageDialog] = useState(false)
  const [editingDashboard, setEditingDashboard] = useState<Dashboard | null>(null)

  const { data: userInfo } = useQuery<UserInfo>({
    queryKey: ['currentUser'],
    queryFn: () => apiClient.get('/auth/me'),
    staleTime: 5 * 60 * 1000,
  })

  const isSystemAdmin = userInfo?.role === 'system_admin' || (
    userInfo?.role === 'organization_admin' && userInfo?.is_system_admin_org
  )

  const orgParams = selectedOrgId ? `?organization_id=${selectedOrgId}` : ''

  const { data: dashboards, isLoading } = useQuery<{ data: Dashboard[] }>({
    queryKey: ['dashboards', selectedOrgId],
    queryFn: () => apiClient.get(`/dashboards${orgParams}`),
  })

  const dashboardList = useMemo(() => dashboards?.data || [], [dashboards?.data])

  // Auto-select first dashboard
  useEffect(() => {
    if (dashboardList.length > 0 && !selectedDashboard) {
      setSelectedDashboard(dashboardList[0].id)
    }
  }, [dashboardList, selectedDashboard])

  // Reset selection when org changes
  useEffect(() => {
    setSelectedDashboard(null)
  }, [selectedOrgId])

  // ESC exits fullscreen
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isFullscreen])

  if (isLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className={isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'space-y-4'}>
      {/* Header */}
      <div className={`flex items-center justify-between ${isFullscreen ? 'px-4 pt-3 pb-2' : ''}`}>
        {!isFullscreen && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
            <p className="mt-1 text-gray-600">{t('subtitle')}</p>
          </div>
        )}
        <div className="flex items-center gap-2">
          {isSystemAdmin && !isFullscreen && (
            <button
              onClick={() => { setEditingDashboard(null); setShowManageDialog(true) }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              <Plus className="w-4 h-4" />
              {t('manage.add')}
            </button>
          )}
          {selectedDashboard && (
            <button
              onClick={() => setIsFullscreen(!isFullscreen)}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
              title={isFullscreen ? t('fullscreen.exit') : t('fullscreen.enter')}
            >
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {dashboardList.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
          <BarChart3 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">{t('dashboards.noDashboards')}</p>
        </div>
      ) : (
        <>
          {/* Tabs (only if multiple dashboards) */}
          {dashboardList.length > 1 && (
            <div className={`flex gap-1 border-b ${isFullscreen ? 'px-4' : ''}`}>
              {dashboardList.map((dashboard) => (
                <button
                  key={dashboard.id}
                  onClick={() => setSelectedDashboard(dashboard.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    selectedDashboard === dashboard.id
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {dashboard.name}
                  {isSystemAdmin && !isFullscreen && (
                    <span className="ml-2 inline-flex gap-1">
                      <Pencil
                        className="w-3 h-3 text-gray-400 hover:text-primary-600"
                        onClick={(e) => { e.stopPropagation(); setEditingDashboard(dashboard); setShowManageDialog(true) }}
                      />
                      <Trash2
                        className="w-3 h-3 text-gray-400 hover:text-red-600"
                        onClick={(e) => { e.stopPropagation(); handleDelete(dashboard.id) }}
                      />
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Single dashboard: show edit/delete inline */}
          {dashboardList.length === 1 && isSystemAdmin && !isFullscreen && (
            <div className="flex gap-2">
              <button
                onClick={() => { setEditingDashboard(dashboardList[0]); setShowManageDialog(true) }}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-primary-600"
              >
                <Pencil className="w-3 h-3" /> {t('manage.edit')}
              </button>
              <button
                onClick={() => handleDelete(dashboardList[0].id)}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-600"
              >
                <Trash2 className="w-3 h-3" /> {t('manage.delete')}
              </button>
            </div>
          )}

          {/* Report embed area */}
          <div className={`bg-white ${isFullscreen ? 'h-[calc(100vh-80px)]' : 'rounded-xl shadow-sm border'}`}>
            {selectedDashboard ? (
              <PowerBIReport
                dashboardId={selectedDashboard}
                orgParams={orgParams}
                isFullscreen={isFullscreen}
              />
            ) : (
              <div className="flex items-center justify-center h-[600px] text-gray-500">
                {t('dashboards.select')}
              </div>
            )}
          </div>
        </>
      )}

      {/* Manage dialog */}
      {showManageDialog && (
        <DashboardManageDialog
          dashboard={editingDashboard}
          orgParams={orgParams}
          onClose={() => { setShowManageDialog(false); setEditingDashboard(null) }}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['dashboards'] })
            setShowManageDialog(false)
            setEditingDashboard(null)
          }}
        />
      )}
    </div>
  )

  function handleDelete(dashboardId: string) {
    if (!confirm(t('manage.confirmDelete'))) return
    apiClient.delete(`/dashboards/${dashboardId}${orgParams}`).then(() => {
      queryClient.invalidateQueries({ queryKey: ['dashboards'] })
      if (selectedDashboard === dashboardId) {
        setSelectedDashboard(null)
      }
    })
  }
}


function PowerBIReport({ dashboardId, orgParams, isFullscreen }: {
  dashboardId: string
  orgParams: string
  isFullscreen: boolean
}) {
  const { t } = useTranslation('reports')
  const reportRef = useRef<Embed | null>(null)

  const { data: embedConfig, isLoading, error } = useQuery<EmbedConfig>({
    queryKey: ['dashboard-embed', dashboardId, orgParams],
    queryFn: () => apiClient.get(`/dashboards/${dashboardId}/embed-config${orgParams}`),
    enabled: !!dashboardId,
    refetchInterval: 50 * 60 * 1000, // Refresh token every 50 min (expires ~60 min)
  })

  const handleEmbedded = useCallback((component: Embed) => {
    reportRef.current = component
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[600px]">
        <LoadingSpinner text={t('dashboards.loading')} />
      </div>
    )
  }

  if (error || !embedConfig) {
    return (
      <div className="flex flex-col items-center justify-center h-[600px] text-red-600 gap-4">
        <p>{t('embed.error')}</p>
      </div>
    )
  }

  return (
    <div className={`w-full ${isFullscreen ? 'h-full' : 'h-[calc(100vh-280px)] min-h-[500px]'}`}>
      <PowerBIEmbed
        embedConfig={{
          type: 'report',
          id: embedConfig.report_id,
          embedUrl: embedConfig.embed_url,
          accessToken: embedConfig.embed_token,
          tokenType: models.TokenType.Embed,
          settings: {
            panes: {
              filters: { expanded: false, visible: true },
              pageNavigation: { visible: true, position: models.PageNavigationPosition.Left },
            },
            navContentPaneEnabled: true,
            background: models.BackgroundType.Transparent,
          },
        }}
        cssClassName="w-full h-full"
        getEmbeddedComponent={handleEmbedded}
      />
    </div>
  )
}


function DashboardManageDialog({ dashboard, orgParams, onClose, onSaved }: {
  dashboard: Dashboard | null
  orgParams: string
  onClose: () => void
  onSaved: () => void
}) {
  const { t } = useTranslation('reports')
  const isEditing = !!dashboard

  const [name, setName] = useState(dashboard?.name || '')
  const [description, setDescription] = useState(dashboard?.description || '')
  const [reportId, setReportId] = useState(dashboard?.powerbi_report_id || '')
  const [workspaceId, setWorkspaceId] = useState('')
  const [displayOrder, setDisplayOrder] = useState(dashboard?.display_order ?? 0)

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.post(`/dashboards${orgParams}`, data),
    onSuccess: onSaved,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      apiClient.patch(`/dashboards/${dashboard!.id}${orgParams}`, data),
    onSuccess: onSaved,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const data: Record<string, unknown> = { name, description: description || null, display_order: displayOrder }

    if (isEditing) {
      if (reportId !== dashboard!.powerbi_report_id) data.powerbi_report_id = reportId
      if (workspaceId) data.powerbi_workspace_id = workspaceId
      updateMutation.mutate(data)
    } else {
      data.powerbi_report_id = reportId
      if (workspaceId) data.powerbi_workspace_id = workspaceId
      createMutation.mutate(data)
    }
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending
  const submitError = createMutation.error || updateMutation.error

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold">
            {isEditing ? t('manage.editTitle') : t('manage.addTitle')}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('manage.name')}</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('manage.description')}</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Report ID</label>
            <input
              type="text"
              value={reportId}
              onChange={(e) => setReportId(e.target.value)}
              required
              placeholder="a2b2b417-4134-4c1b-84bc-fa57272598f8"
              className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Workspace ID</label>
            <input
              type="text"
              value={workspaceId}
              onChange={(e) => setWorkspaceId(e.target.value)}
              placeholder="1688bdc7-c3d8-431d-a8bc-9be499ca2e6c"
              className="w-full px-3 py-2 border rounded-lg font-mono text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="text-xs text-gray-500 mt-1">{t('manage.workspaceHint')}</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('manage.displayOrder')}</label>
            <input
              type="number"
              value={displayOrder}
              onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)}
              className="w-20 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {submitError && (
            <p className="text-sm text-red-600">{t('manage.error')}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border rounded-lg hover:bg-gray-50"
            >
              {t('manage.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting ? t('manage.saving') : (isEditing ? t('manage.save') : t('manage.create'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
